import React, { useEffect, useRef, useState, useMemo } from "react"
import { useLocation, useNavigate } from "react-router-dom"
import axios from "axios"
import { useChatHistory } from "../../contexts/ChatHistoryContext"
import { useOnboarding } from "../../contexts/OnboardingContext"
import { marked } from "marked";
import DOMPurify from "dompurify";
import avatar from "../../assets/images/users/avatar-9.jpg"
import "./ChatBot.css"
import ChatHistorySidebar from "../../components/ChatHistory/ChatHistorySidebar"
import { Menu, X } from "lucide-react"
import { ai_api } from "../../utils/variables"
import { useAnalytics, ANALYTICS_EVENTS } from "../../analytics"

export default function ChatBot() {
  const { trackEvent } = useAnalytics()
  const {
    activeConversation,
    updateMessages,
    createNewChat,
    activeChatId,
    addMessage,
    clearAllHistory,
    updateConversationId,
    refreshConversations,
  } = useChatHistory()

  const [loading, setLoading] = useState(false)
  const messagesEndRef = useRef(null)
  const messagesContainerRef = useRef(null)

  const [input, setInput] = useState("")
  const [sidebarOpen, setSidebarOpen] = useState(false)
  const conversationIdRef = useRef(null)
  const previousChatIdRef = useRef(activeChatId)

  const location = useLocation()
  const initialPromptSentRef = useRef(false)
  const navigate = useNavigate()
  const [showInitialClose, setShowInitialClose] = useState(false)
  const { markPageCompleted } = useOnboarding()
  const isInitialModal = !!location?.state?.initialPrompt


  // Static welcome message for first-time AI screen
  const WELCOME_MESSAGE = "Welcome to KEYQUEST - your comprehensive platform for monitoring and analyzing applications hosted on the Metastore, Steam, and Apple Vision Pro. KEYQUEST provides a suite of tools like App Keyword Explorer, Genre Explorer, App Tracker, Category Tracker, and Keyword Tracker for exploring app trends, analyzing keyword performance, and monitoring insights across Metastore, Steam, and Apple Vision Pro. Whether you're a seasoned analyst or a first-time user, KEYQUEST is designed to be intuitive and powerful, offering you valuable insights into app data."

  // Local messages state for first-time AI modal.
  // This ensures that even if chat history syncing changes, the user always
  // sees their ongoing conversation in the onboarding popup.
  const [modalMessages, setModalMessages] = useState([])

  // Get messages from active conversation
  const messages = useMemo(() => {
    return activeConversation?.messages || []
  }, [activeConversation])

  // Which messages to render in the UI
  const visibleMessages = isInitialModal ? modalMessages : messages

  // Load conversation_id from active chat when switching chats
  useEffect(() => {
    // If activeChatId changed to a different chat, load that chat's conversation_id
    if (previousChatIdRef.current !== activeChatId) {
      // Load conversation_id from the active conversation
      conversationIdRef.current = activeConversation?.conversationId || null
      previousChatIdRef.current = activeChatId
    } else if (activeConversation && conversationIdRef.current !== activeConversation?.conversationId) {
      // Sync if conversation_id was updated elsewhere
      conversationIdRef.current = activeConversation?.conversationId || null
    }
  }, [activeChatId, activeConversation])

  // When navigated with initialPrompt, add a body class to blur the sidebar and scroll to top
  useEffect(() => {
    const initialPrompt = location?.state?.initialPrompt
    if (initialPrompt) {
      document.body.classList.add("chatbot-initial-prompt")
      setShowInitialClose(true)

      // Scroll to top of the page when AI screen opens
      window.scrollTo({ top: 0, behavior: 'instant' })

      // Also scroll the chat messages container to top
      setTimeout(() => {
        const messagesContainer = document.querySelector('.chat-messages-container')
        if (messagesContainer) {
          messagesContainer.scrollTop = 0
        }
        // Also try via ref if available
        if (messagesContainerRef.current) {
          messagesContainerRef.current.scrollTop = 0
        }
      }, 100)
    }
    return () => {
      // remove class on unmount or route change
      document.body.classList.remove("chatbot-initial-prompt")
      setShowInitialClose(false)
    }
  }, [location])

  const handleCloseInitial = async () => {
    // Mark AI screen as completed when user closes it
    try {
      await markPageCompleted('ai_screen')
      // Set a session flag to prevent immediate redirect back
      sessionStorage.setItem('ai_screen_dismissed', 'true')
    } catch (e) {
      // If marking fails, still allow closing
      console.error('Error marking AI screen as completed:', e)
    }

    // remove the body class and navigate to keyword explorer
    try {
      document.body.classList.remove("chatbot-initial-prompt")
    } catch (e) { }
    navigate("/keyword-analysis")
  }
  function isMarkdownTable(text) {
    return /\|.+\|/.test(text) && /[-]+/.test(text);
  }

  function convertMarkdownToHtmlTable(md) {
    const rawHtml = marked(md);
    return DOMPurify.sanitize(rawHtml);
  }

  // const handleSend = () => {
  //   if (!input.trim()) return

  //   const updated = [...messages, { from: "You", text: input }]
  //   setMessages(updated)
  //   setInput("")
  //   setLoading(true) // ✅ START loading before bot reply

  //   setTimeout(() => {
  //     setMessages(prev => [
  //       ...prev,
  //       { from: "bot", text: "I'm just a dummy bot 😅" },
  //     ])
  //     setLoading(false) // ✅ STOP loading after bot response
  //   }, 1000)
  // }

  // Apply when API is used

  const PREDEFINED_QUESTIONS = [
    "What tools does KeyQuest offer?",
    "How do I track keywords for my app?",
    "What apps are trending on Steam?",
    "How do I explore top categories on Metastore?",
    "What are the top apps on Apple Vision Pro?",
    "How can I monitor my app's performance?",
  ]

  const handleSend = async (textOverride) => {
    const userInput = textOverride ?? input
    if (!userInput.trim()) return

    // addMessage handles chat creation if needed
    const currentChatId = addMessage({ from: "You", text: userInput })

    // Keep local copy for first-time AI modal
    if (isInitialModal) {
      setModalMessages(prev => [...prev, { from: "You", text: userInput }])
    }

    if (!textOverride) setInput("")
    setLoading(true)

    try {
      const response = await fetchBotResponse(userInput)
      addMessage({ from: "bot", text: response }, currentChatId)

      if (isInitialModal) {
        setModalMessages(prev => [...prev, { from: "bot", text: response }])
      }

      trackEvent(ANALYTICS_EVENTS.FEATURE.ASSISTANT_CHAT_MESSAGE_SENT, { surface: "chatbot_page" })
    } catch (err) {
      const fallback = "Something went wrong."
      addMessage({ from: "bot", text: fallback }, currentChatId)

      if (isInitialModal) {
        setModalMessages(prev => [...prev, { from: "bot", text: fallback }])
      }
    } finally {
      setLoading(false)
    }
  }

  // Show welcome message when navigated with state.initialPrompt (for new users)
  useEffect(() => {
    const initialPrompt = location?.state?.initialPrompt
    if (initialPrompt && !initialPromptSentRef.current) {
      initialPromptSentRef.current = true
      // First-time AI onboarding:
      // 1) Clear any previous chat history so the user sees a fresh screen.
      // 2) Show a static welcome message WITHOUT calling the API.
      // 3) Only call the API when the user sends their first message.
      try {
        clearAllHistory()
        conversationIdRef.current = null
      } catch (e) {
        // If clearing fails, still proceed with creating a new conversation
        console.error("Failed to clear chat history for initial AI screen:", e)
      }

      createNewChat("Welcome to KeyQuest", [
        { from: "bot", text: WELCOME_MESSAGE },
      ])
      setModalMessages([{ from: "bot", text: WELCOME_MESSAGE }])
    }
    // we intentionally omit messages from deps to avoid re-triggering on every message change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location])

  async function fetchBotResponse(inputValue) {
    const gamedata = { query: inputValue }
    // attach conversation_id for follow-up queries within this chat
    if (conversationIdRef.current) {
      gamedata.conversation_id = conversationIdRef.current
    }

    const res = await axios.post(`${ai_api}/chat/`, gamedata, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem("authToken")}`,
      },
    })
    // persist conversation_id from API to both ref and chat object
    if (res.data?.conversation_id && activeChatId) {
      console.log('Setting conversation_id:', res.data.conversation_id, 'for chat:', activeChatId) // Debug
      conversationIdRef.current = res.data.conversation_id
      // Save conversation_id to the chat so it persists when switching back
      updateConversationId(activeChatId, res.data.conversation_id)
      // Refresh conversations from API to sync with backend
      refreshConversations()
    }
    return res.data?.response
  }

  useEffect(() => {
    // For initial modal, scroll to top instead of bottom
    if (isInitialModal && visibleMessages.length > 0) {
      // Use both querySelector and ref for reliability
      const messagesContainer = document.querySelector('.chat-messages-container')
      if (messagesContainer) {
        messagesContainer.scrollTop = 0
      }
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = 0
      }
    } else if (!isInitialModal) {
      // For regular chats, scroll to bottom by setting scrollTop
      // This prevents the page from scrolling and only scrolls the container
      if (messagesContainerRef.current) {
        messagesContainerRef.current.scrollTop = messagesContainerRef.current.scrollHeight
      }
      // Fallback to querySelector
      const messagesContainer = document.querySelector('.chat-messages-container')
      if (messagesContainer) {
        messagesContainer.scrollTop = messagesContainer.scrollHeight
      }
    }
  }, [visibleMessages, isInitialModal])




  const formatText = text => {
    if (!text) return null

    const ftext = text
      .replace(/\*\*(.*?)\*\*/g, "<strong>$1</strong>") // Bold (**text**)
      .replace(/# (.*?)\n/g, '<h5 style="margin-top: 16px">$1</h5>') // Headings (# Heading)
      .replace(/- \*\*(.*?)\*\*:/g, "<li><b>$1:</b>") // List with bold (**text:**)
      .replace(/- (.*?)\n/g, "<li>$1</li>") // Normal list item (- text)
      .replace(/\n/g, "<br/>") // New lines to `<br/>`
    return <span dangerouslySetInnerHTML={{ __html: ftext }} />
  }

  return (
    <div className={`chatbot-app ${isInitialModal ? "chatbot-modal" : ""}`}>
      {/* Mobile Sidebar Toggle (hidden in initial modal popup) */}
      {!isInitialModal && (
        <button
          className="mobile-sidebar-toggle"
          onClick={() => setSidebarOpen(!sidebarOpen)}
          aria-label="Toggle chat history"
        >
          {sidebarOpen ? <X size={24} /> : <Menu size={24} />}
        </button>
      )}

      {/* Chat History Sidebar (hidden in initial modal popup) */}
      {!isInitialModal && (
        <div className={`chatbot-sidebar ${sidebarOpen ? 'open' : ''}`}>
          <ChatHistorySidebar onChatSelect={() => setSidebarOpen(false)} />
        </div>
      )}

      {/* Sidebar Overlay for mobile (hidden in initial modal popup) */}
      {!isInitialModal && sidebarOpen && (
        <div
          className="sidebar-overlay"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Main Chat Area */}
      <div className="chatbot-main">
        {/* Header */}
        <div className="chatbot-header">
          <div className="header-content">
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap' }}>
              <h1 className="page-title" style={{ margin: 0 }}>KeyQuest AI</h1>
              <span style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
                background: 'linear-gradient(135deg, #f59e0b, #d97706)',
                color: '#fff',
                fontSize: '11px',
                fontWeight: 700,
                letterSpacing: '0.06em',
                textTransform: 'uppercase',
                padding: '4px 10px',
                borderRadius: '4px',
                flexShrink: 0,
                boxShadow: '0 1px 4px rgba(217,119,6,0.4)',
              }}>
                <i className="fas fa-crown" style={{ fontSize: '10px' }} />
                Paid Plan
              </span>
            </div>
          </div>
        </div>

        {/* Chat Interface */}
        <div className="chatbot-container">
          {/* Close button inside chatbot container */}
          {(showInitialClose || location?.state?.initialPrompt) && (
            <div className="initial-close-container">
              <button
                className="initial-close-btn in-chat"
                aria-label="Close and return to dashboard"
                onClick={handleCloseInitial}
              >
                ×
              </button>
              <div className="initial-close-label">
                Close or Continue chat.
              </div>
            </div>
          )}

          {/* Chat Messages Area */}
          <div className="chat-messages-container" ref={messagesContainerRef}>
            <div className="chat-messages">
              {/* Predefined question chips — shown throughout the onboarding modal, hidden only while loading */}
              {isInitialModal && !loading && (
                <div className="predefined-questions">
                  <p className="predefined-questions-label">
                    {modalMessages.some(m => m.from === "You") ? "Try another question:" : "Not sure where to start? Try one of these:"}
                  </p>
                  <div className="predefined-questions-chips">
                    {PREDEFINED_QUESTIONS.map((q, i) => (
                      <button
                        key={i}
                        className="predefined-question-btn"
                        onClick={() => handleSend(q)}
                      >
                        {q}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {!activeChatId || visibleMessages.length === 0 ? null : (
                visibleMessages.map((message, idx) => (
                    <React.Fragment key={"message_" + idx}>
                      <div
                        className={`message-item ${message.from === "You" ? "user-message" : "ai-message"}`}
                      >
                        {message.from !== "You" && (
                          <div className="message-sender-label">
                            AI Assistant
                          </div>
                        )}

                        <div className="message-row">
                          {/* Avatar */}
                          <div className="message-avatar">
                            {message.from === "You" ? (
                              <div className="user-avatar">
                                <img src={avatar} alt="User" />
                              </div>
                            ) : (
                              <div className="ai-avatar">
                                <img src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=32&h=32&fit=crop&crop=face&rounded=full" alt="AI Assistant" />
                              </div>
                            )}
                          </div>

                          {/* Message Content */}
                          <div className="message-content">
                            <div className="message-bubble">
                              {isMarkdownTable(message.text) ? (
                                <div
                                  className="markdown-table"
                                  dangerouslySetInnerHTML={{
                                    __html: convertMarkdownToHtmlTable(message.text),
                                  }}
                                />
                              ) : (
                                <div className="message-text">
                                  {formatText(message.text)}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>

                        {message.from === "You" && (
                          <div className="message-sender-label user-label">
                            User
                          </div>
                        )}
                      </div>
                      {/* Day divider after the first message */}
                      {idx === 0 && (
                        <div className="day-divider">Today</div>
                      )}
                    </React.Fragment>
                  ))
              )}

              {/* Loading indicator */}
              {loading && (
                <div className="message-item ai-message">
                  <div className="message-sender-label">
                    AI Assistant
                  </div>

                  <div className="message-row">
                    <div className="message-avatar">
                      <div className="ai-avatar">
                        <img src="https://images.unsplash.com/photo-1485827404703-89b55fcc595e?w=32&h=32&fit=crop&crop=face&rounded=full" alt="AI Assistant" />
                      </div>
                    </div>

                    <div className="message-content">
                      <div className="message-bubble loading-bubble">
                        <div className="loading-dots">
                          <div className="dot"></div>
                          <div className="dot"></div>
                          <div className="dot"></div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div ref={messagesEndRef}></div>
            </div>
          </div>

          {/* Input Area */}
          <div className="chat-input-container">
            <div className="input-wrapper">
              <input
                type="text"
                className="chat-input"
                value={input}
                onKeyDown={e => e.key === "Enter" && handleSend()}
                onChange={e => setInput(e.target.value)}
                placeholder="Enter Message..."
              />
              <button
                className="send-button"
                onClick={handleSend}
                disabled={!input.trim()}
              >
                <svg viewBox="0 0 24 24" fill="currentColor">
                  <path d="M2.01 21L23 12 2.01 3 2 10l15 2-15 2z" />
                </svg>
                Send
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
